import React, { useEffect, useState } from "react";
import moment from "moment";
import { diffBetweenStatusTimestamps } from "../../../utils/statusTimelineUtils";
import styles from "./HistoricoOperacional.module.css";

function AuditRow({ evento }) {
  return (
    <footer className={styles.auditRow}>
      {evento.local && (
        <>
          <div className={styles.locationItem}>
            <i className="fa fa-map-marker-alt" />
            <span>{evento.local}</span>
          </div>
          <div className={styles.auditSeparator} />
        </>
      )}
      <div className={styles.auditItem}>
        <i className="fa fa-user" />
        <span>{evento.usuario}</span>
      </div>
      <div className={styles.auditSeparator} />
      <div className={styles.auditItem}>
        <i className="fa fa-desktop" />
        <span>{evento.ambiente}</span>
      </div>
    </footer>
  );
}

function EventTimeContext({ evento }) {
  return (
    <div className={styles.eventTimeContext}>
      <div className={styles.dateTime}>
        <strong>{moment(evento.horario).format("HH:mm")}</strong>
        <span>{moment(evento.horario).format("DD/MM/YYYY")}</span>
      </div>

      <div className={styles.duration}>
        <i className="fa fa-history" />
        <div>
          <span>Tempo no status</span>
          <strong>{evento.tempoNoStatus}</strong>
        </div>
      </div>
    </div>
  );
}

function OperationTime({ tempoOperacao }) {
  const [agora, setAgora] = useState(() => moment());

  const emAndamento = !tempoOperacao.fim;

  useEffect(() => {
    if (!emAndamento) return undefined;

    const intervalo = setInterval(() => setAgora(moment()), 1000);
    return () => clearInterval(intervalo);
  }, [emAndamento]);

  const valor = emAndamento
    ? diffBetweenStatusTimestamps(tempoOperacao.inicio, agora)
    : tempoOperacao.duracao;

  return (
    <div className={styles.operationDuration}>
      <i className="fa fa-history" />
      <div>
        <span>Tempo de Operação</span>
        <strong>{valor}</strong>
      </div>
    </div>
  );
}

function IdsValue({ ids }) {
  return (
    <div className={styles.idsValue}>
      {ids.map((id, index) => (
        <React.Fragment key={id}>
          {index > 0 && <div className={styles.idsSeparator} />}
          <strong>{id}</strong>
        </React.Fragment>
      ))}
    </div>
  );
}

function DetailsRow({ items }) {
  if (!items || items.length === 0) return null;

  return (
    <div className={styles.summaryDetails}>
      {items.map((item) => (
        <div key={item.label} className={styles.summaryDetailItem}>
          <span>{item.label}</span>
          <strong title={typeof item.value === "string" ? item.value : undefined}>{item.value}</strong>
        </div>
      ))}
    </div>
  );
}

function SummaryDetails({ motorista, info }) {
  const ids = [
    info?.idProgramacao && `#${info.idProgramacao}`,
    info?.idLote && `#${info.idLote}`,
    info?.idAgendamento && `#${info.idAgendamento}`,
  ].filter(Boolean);

  const linhaMotorista = [
    motorista?.celular && { label: "Celular", value: motorista.celular },
    motorista?.placas?.length > 0 && { label: "Placas", value: motorista.placas.join(" / ") },
    ids.length > 0 && { label: "Programação / Lote / Agendamento", value: <IdsValue ids={ids} /> },
  ].filter(Boolean);

  const linhaEmbarcadorArmazem = [
    info?.embarcador && { label: "Embarcador", value: info.embarcador },
    info?.armazemOrigem && { label: "Armazém de Origem", value: info.armazemOrigem },
    info?.armazemDestino && { label: "Armazém de Destino", value: info.armazemDestino },
  ].filter(Boolean);

  if (linhaMotorista.length === 0 && linhaEmbarcadorArmazem.length === 0) return null;

  return (
    <div className={styles.summaryDetailsGroup}>
      <DetailsRow items={linhaMotorista} />
      <DetailsRow items={linhaEmbarcadorArmazem} />
    </div>
  );
}

function SummaryHeader({ motorista, tempoOperacao, info }) {
  return (
    <header className={styles.summaryCard}>
      <div className={styles.summaryCardTopRow}>
        {motorista?.nome && (
          <div className={styles.motoristaInfo}>
            <i className="fa fa-user-circle" />
            <div>
              <strong>{motorista.nome}</strong>
              {motorista.cpf && <span>{motorista.cpf}</span>}
            </div>
          </div>
        )}

        {tempoOperacao && <OperationTime tempoOperacao={tempoOperacao} />}
      </div>

      <SummaryDetails motorista={motorista} info={info} />
    </header>
  );
}

export default function HistoricoOperacional({ grupos, tempoOperacao, motorista, info }) {
  if (!grupos || grupos.length === 0) {
    return <div className={styles.empty}>Nenhum dado encontrado</div>;
  }

  return (
    <div className={styles.timeline}>
      {(motorista?.nome || tempoOperacao) && (
        <SummaryHeader motorista={motorista} tempoOperacao={tempoOperacao} info={info} />
      )}

      {grupos.map((grupo, groupIndex) => {
        const isLast = groupIndex === grupos.length - 1;
        const { principal } = grupo;

        return (
          <article key={grupo.key} className={styles.statusGroup}>
            <div className={styles.mainTimeline}>
              <div className={`${styles.mainDot} ${isLast ? styles.mainDotCurrent : ""}`}>
                <i className={`fa ${principal.icone}`} />
              </div>
              {!isLast && <div className={styles.mainLine} />}
            </div>

            <div className={`${styles.statusCard} ${isLast ? styles.statusCardCurrent : ""}`}>
              {isLast && <div className={styles.currentFlag}>Status atual</div>}

              <div className={styles.principalEvent}>
                <div className={styles.eventMainRow}>
                  <div className={styles.eventIdentity}>
                    <div className={`${styles.statusBadge} ${styles[`badge_${principal.type}`]}`}>
                      <i className={`fa ${principal.icone}`} />
                      {principal.rotulo}
                    </div>
                  </div>

                  <EventTimeContext evento={principal} />
                </div>

                <p className={styles.eventDescription}>{principal.descricao}</p>

                <AuditRow evento={principal} />
              </div>

              {grupo.substatusEventos.length > 0 && (
                <div className={styles.substatusArea}>
                  {grupo.substatusEventos.map((evento) => (
                    <div className={styles.substatusEvent} key={evento.key}>
                      <div className={styles.eventMainRow}>
                        <div className={styles.eventIdentity}>
                          <div className={`${styles.substatusBadge} ${styles[`badge_${evento.type}`]}`}>
                            {evento.rotulo}
                          </div>
                        </div>

                        <EventTimeContext evento={evento} />
                      </div>

                      <p className={styles.eventDescription}>{evento.descricao}</p>

                      <AuditRow evento={evento} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}
